import { supabase } from '../supabase';
import { invalidateAuthTokenCache } from '../authTokenCache';

/**
 * Gera a chave de cache para recomendações de um usuário
 */
export function getRecommendationsCacheKey(userId: string, algorithm: string): string {
  return `recommendations:${userId}:${algorithm}`;
}

/**
 * Obtém um valor do cache
 */
export async function getCache<T>(key: string): Promise<T | null> {
  const cacheService = new CacheService();
  return await cacheService.get(key) as T | null;
}

/**
 * Define um valor no cache
 */
export async function setCache(key: string, value: any, expiryInSeconds?: number): Promise<void> {
  const cacheService = new CacheService();
  await cacheService.set(key, value, expiryInSeconds);
}

/**
 * Invalida uma entrada de cache
 */
export async function invalidateCache(key: string): Promise<void> {
  const cacheService = new CacheService();
  await cacheService.delete(key);
}

/**
 * Serviço para gerenciamento de cache e limpeza de recomendações
 */
export class CacheService {
  // Cache de jogos populares
  private popularGamesCache: Record<string, any> = {};
  
  // Cache de IDs de jogos por usuário
  private userGameIdsCache: Record<string, {
    timestamp: number;
    ids: {
      ownedIds: string[];
      likedIds: string[];
      dislikedIds: string[];
      interactedIds: string[];
    }
  }> = {};
  
  // Cache em memória para recomendações e outros dados
  private memoryCache: Map<string, {
    data: any,
    expiry: number | null
  }> = new Map();
  
  // Contagem de requisições por período de tempo
  private requestCounts: Map<string, {
    count: number,
    resetTime: number
  }> = new Map();
  
  // Tempo de expiração do cache (15 minutos)
  private cacheExpirationMs = 15 * 60 * 1000;
  
  // Limites de requisições (por minuto)
  private readonly REQUEST_LIMIT = 50;
  private readonly REQUEST_WINDOW_MS = 60 * 1000; // 1 minuto
  
  /**
   * Verifica e incrementa a contagem de requisições para um determinado tipo
   * @param requestType Tipo de requisição para controle de limite
   * @returns true se a requisição pode prosseguir, false se atingiu o limite
   */
  private checkRequestLimit(requestType: string): boolean {
    const now = Date.now();
    const key = `req:${requestType}`;
    
    // Obter contador atual ou criar um novo
    let counterData = this.requestCounts.get(key);
    
    if (!counterData) {
      counterData = {
        count: 0,
        resetTime: now + this.REQUEST_WINDOW_MS
      };
      this.requestCounts.set(key, counterData);
    }
    
    // Verificar se o período de contagem resetou
    if (now > counterData.resetTime) {
      counterData.count = 0;
      counterData.resetTime = now + this.REQUEST_WINDOW_MS;
    }
    
    // Verificar se atingiu o limite
    if (counterData.count >= this.REQUEST_LIMIT) {
      console.warn(`Limite de requisições para ${requestType} atingido. Aguarde ${Math.ceil((counterData.resetTime - now) / 1000)}s.`);
      return false;
    }
    
    // Incrementar contador
    counterData.count++;
    return true;
  }
  
  /**
   * Obtém os IDs dos jogos do usuário (com cache)
   */
  public async getUserGameIds(userId: string): Promise<{
    ownedIds: string[];
    likedIds: string[];
    dislikedIds: string[];
    interactedIds: string[];
  }> {
    // Verificar se temos no cache e se não expirou
    if (
      this.userGameIdsCache[userId] && 
      (Date.now() - this.userGameIdsCache[userId].timestamp) < this.cacheExpirationMs
    ) {
      return this.userGameIdsCache[userId].ids;
    }
    
    // Verificar se não atingiu o limite de requisições
    if (!this.checkRequestLimit('user_game_ids')) {
      // Se atingiu o limite, retornar o cache antigo se existir
      if (this.userGameIdsCache[userId]) {
        console.log('Usando cache expirado devido a limite de requisições');
        return this.userGameIdsCache[userId].ids;
      }
      
      // Se não tiver nada em cache, retornar valores vazios
      return {
        ownedIds: [],
        likedIds: [],
        dislikedIds: [],
        interactedIds: []
      };
    }
    
    // Se não temos ou expirou, buscar do banco
    const ownedIds = await this.getOwnedGameIds(userId);
    const likedIds = await this.getLikedGameIds(userId);
    const dislikedIds = await this.getDislikedGameIds(userId);
    const interactedIds = await this.getAllInteractedGameIds(userId);
    
    // Atualizar cache
    this.userGameIdsCache[userId] = {
      timestamp: Date.now(),
      ids: { ownedIds, likedIds, dislikedIds, interactedIds }
    };
    
    return this.userGameIdsCache[userId].ids;
  }
  
  /**
   * Limpa o cache para um usuário específico
   */
  public clearUserCache(userId: string): void {
    delete this.userGameIdsCache[userId];
    // Também limpar o cache de recomendações
    this.delete(`recommendations:${userId}`);
  }
  
  /**
   * Limpa todo o cache
   */
  public clearAllCache(): void {
    this.popularGamesCache = {};
    this.userGameIdsCache = {};
    this.memoryCache.clear();
    this.requestCounts.clear();
    // Também limpar o cache de autenticação para garantir consistência
    invalidateAuthTokenCache();
  }
  
  /**
   * Obtém IDs de jogos que o usuário possui
   */
  private async getOwnedGameIds(userId: string): Promise<string[]> {
    try {
      // Verificar se não atingiu o limite de requisições
      if (!this.checkRequestLimit('user_games_owned')) {
        return [];
      }
      
      const { data, error } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .eq('owned', true);
        
      if (error) {
        console.error('Erro ao obter jogos que o usuário possui:', error);
        return [];
      }
      
      return data?.map(item => item.game_id) || [];
    } catch (error) {
      console.error('Erro ao processar jogos do usuário:', error);
      return [];
    }
  }
  
  /**
   * Obtém IDs de jogos que o usuário gostou
   */
  private async getLikedGameIds(userId: string): Promise<string[]> {
    try {
      // Verificar se não atingiu o limite de requisições
      if (!this.checkRequestLimit('user_games_liked')) {
        return [];
      }
      
      const { data, error } = await supabase
        .from('recommendations')
        .select('game_id')
        .eq('user_id', userId)
        .eq('liked', true);
        
      if (error) {
        console.error('Erro ao obter jogos que o usuário gostou:', error);
        return [];
      }
      
      return data?.map(item => item.game_id) || [];
    } catch (error) {
      console.error('Erro ao processar jogos que o usuário gostou:', error);
      return [];
    }
  }
  
  /**
   * Obtém IDs de jogos que o usuário não gostou
   */
  private async getDislikedGameIds(userId: string): Promise<string[]> {
    try {
      // Verificar se não atingiu o limite de requisições
      if (!this.checkRequestLimit('user_games_disliked')) {
        return [];
      }
      
      const { data, error } = await supabase
        .from('recommendations')
        .select('game_id')
        .eq('user_id', userId)
        .eq('liked', false);
        
      if (error) {
        console.error('Erro ao obter jogos que o usuário não gostou:', error);
        return [];
      }
      
      return data?.map(item => item.game_id) || [];
    } catch (error) {
      console.error('Erro ao processar jogos que o usuário não gostou:', error);
      return [];
    }
  }
  
  /**
   * Obtém todos os IDs de jogos com os quais o usuário interagiu
   */
  private async getAllInteractedGameIds(userId: string): Promise<string[]> {
    try {
      // Verificar se não atingiu o limite de requisições
      if (!this.checkRequestLimit('user_games_interacted')) {
        return [];
      }
      
      // Obter jogos com qualquer rating
      const { data: ratedGames, error: ratingError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .not('rating', 'is', null);
        
      if (ratingError) {
        console.error('Erro ao obter jogos avaliados:', ratingError);
      }
      
      // Obter jogos com qualquer interação na tabela de recomendações
      const { data: feedbackGames, error: feedbackError } = await supabase
        .from('recommendations')
        .select('game_id')
        .eq('user_id', userId)
        .or('liked.not.is.null,viewed.eq.true');
        
      if (feedbackError) {
        console.error('Erro ao obter jogos com feedback:', feedbackError);
      }
      
      // Obter jogos com interações diretas
      const { data: interactions, error: interactionError } = await supabase
        .from('user_interactions')
        .select('game_id')
        .eq('user_id', userId);
        
      if (interactionError) {
        console.error('Erro ao obter interações com jogos:', interactionError);
      }
      
      // Combinar e remover duplicatas com Set
      const ratedIds = (ratedGames || []).map(item => item.game_id);
      const feedbackIds = (feedbackGames || []).map(item => item.game_id);
      const interactionIds = (interactions || []).map(item => item.game_id);
      
      return [...new Set([...ratedIds, ...feedbackIds, ...interactionIds])];
    } catch (error) {
      console.error('Erro ao processar jogos com interação:', error);
      return [];
    }
  }
  
  /**
   * Remove recomendações antigas
   */
  public async cleanupOldRecommendations(daysThreshold: number = 30): Promise<void> {
    try {
      // Verificar se não atingiu o limite de requisições
      if (!this.checkRequestLimit('cleanup_recommendations')) {
        console.log('Limite de requisições atingido para limpeza de recomendações. Tentando novamente mais tarde.');
        return;
      }
      
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysThreshold);
      
      // Remover recomendações mais antigas que o limite
      const { error } = await supabase
        .from('recommendations')
        .delete()
        .lt('created_at', cutoffDate.toISOString());
        
      if (error) {
        console.error(`Erro ao remover recomendações antigas: ${error.message}`);
        return;
      }
      
      console.log(`Limpeza de recomendações concluída. Removidas recomendações anteriores a ${cutoffDate.toISOString()}`);
    } catch (error) {
      console.error('Erro ao limpar recomendações antigas:', error);
    }
  }

  /**
   * Métodos para operações no cache em memória
   */
  public async get(key: string): Promise<any> {
    try {
      const cachedItem = this.memoryCache.get(key);
      
      if (!cachedItem) {
        return null;
      }
      
      // Verificar expiração
      if (cachedItem.expiry && cachedItem.expiry < Date.now()) {
        // Cache expirado, remover
        this.delete(key);
        return null;
      }
      
      return cachedItem.data;
    } catch (error) {
      console.error('Erro ao recuperar do cache:', error);
      return null;
    }
  }

  /**
   * Salva um valor no cache com expiração opcional
   * @param key Chave do cache
   * @param value Valor a ser armazenado
   * @param expiryInSeconds Tempo de expiração em segundos (opcional)
   */
  public async set(key: string, value: any, expiryInSeconds?: number): Promise<void> {
    try {
      const expiry = expiryInSeconds ? Date.now() + (expiryInSeconds * 1000) : null;
      
      this.memoryCache.set(key, {
        data: value,
        expiry: expiry
      });
    } catch (error) {
      console.error('Erro ao salvar no cache:', error);
    }
  }

  /**
   * Remove um item do cache
   * @param key Chave do item a ser removido
   */
  public async delete(key: string): Promise<void> {
    try {
      this.memoryCache.delete(key);
    } catch (error) {
      console.error('Erro ao deletar do cache:', error);
    }
  }

  /**
   * Limpa todo o cache ou itens expirados
   * @param onlyExpired Se true, remove apenas itens expirados
   */
  public async clear(onlyExpired: boolean = false): Promise<void> {
    try {
      if (onlyExpired) {
        // Remover apenas itens expirados
        const now = Date.now();
        for (const [key, value] of this.memoryCache.entries()) {
          if (value.expiry && value.expiry < now) {
            this.memoryCache.delete(key);
          }
        }
        return;
      }
      
      // Remover todo o cache
      this.memoryCache.clear();
      // Resetar também contadores de requisições
      this.requestCounts.clear();
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
    }
  }
  
  /**
   * Ajusta o tempo de expiração do cache
   * @param timeInMs Novo tempo de expiração em milissegundos
   */
  public setExpirationTime(timeInMs: number): void {
    if (timeInMs > 0) {
      this.cacheExpirationMs = timeInMs;
    }
  }
} 